import React, { useEffect, useRef, useState } from 'react';
import { CheckCircle2, Loader2, MailCheck, ShieldCheck } from 'lucide-react';
import { axiosInstance } from '../App';
import AuthLayout from '../components/auth/AuthLayout';

const ConfirmEmailChange = () => {
  const headingRef = useRef(null);
  const [token] = useState(() => new URLSearchParams(window.location.hash.replace(/^#/, '')).get('token') || '');
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(Boolean(token));
  const [confirming, setConfirming] = useState(false);
  const [complete, setComplete] = useState(null);
  const [error, setError] = useState(token ? '' : 'This email-change link is incomplete.');

  useEffect(() => {
    headingRef.current?.focus();
    if (token && window.location.hash) window.history.replaceState({}, document.title, window.location.pathname);
    if (!token) return;
    axiosInstance.post('/auth/email-change/inspect', { token })
      .then(({ data }) => setDetails(data))
      .catch(requestError => setError(requestError.response?.data?.detail || 'This email-change link is invalid or has expired.'))
      .finally(() => setLoading(false));
  }, [token]);

  const confirm = async () => {
    setConfirming(true);
    setError('');
    try {
      const { data } = await axiosInstance.post('/auth/email-change/confirm', { token });
      localStorage.removeItem('token');
      setComplete(data);
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'This email-change link is invalid or has expired.');
    } finally {
      setConfirming(false);
    }
  };

  return <AuthLayout><div className="enterprise-auth-card"><div className="enterprise-auth-step">
    <p className="enterprise-auth-kicker">Secure account update</p>
    <h2 ref={headingRef} tabIndex="-1">{complete ? 'Sign-in email changed' : 'Confirm your new sign-in email'}</h2>
    {loading ? <div className="enterprise-auth-organisation"><Loader2 className="animate-spin" size={19}/><span>Checking this secure link…</span></div> : complete ? <>
      <div className="enterprise-auth-organisation"><CheckCircle2 size={19}/><span>Your profile and access were preserved</span></div>
      <p className="enterprise-auth-intro">Sign in with <strong>{complete.new_email}</strong> and your existing password. Your previous sessions have been signed out.</p>
      <a className="enterprise-auth-primary enterprise-auth-link-primary" href="/auth">Sign in with the new email</a>
    </> : details ? <>
      <div className="enterprise-auth-organisation"><ShieldCheck size={19}/><span>{details.organization_name}</span></div>
      <div className="rounded-xl border border-slate-200 p-4 text-sm">
        <p className="text-slate-500">Current email</p><p className="font-medium text-slate-900">{details.current_email}</p>
        <p className="mt-3 text-slate-500">New email</p><p className="break-all font-medium text-slate-900">{details.new_email}</p>
      </div>
      <p className="enterprise-auth-intro">Confirming changes only the login email. Your password, profile, UI access, teams and history remain attached to the same account. Existing sessions will be signed out.</p>
      {error && <p className="enterprise-auth-error" role="alert">{error}</p>}
      <button className="enterprise-auth-primary" type="button" disabled={confirming} onClick={confirm}>{confirming ? <><Loader2 className="animate-spin" size={17}/>Confirming…</> : <><MailCheck size={17}/>Confirm email change</>}</button>
    </> : <><p className="enterprise-auth-error" role="alert">{error}</p><a className="enterprise-auth-back" href="/auth">Return to sign in</a></>}
  </div></div></AuthLayout>;
};

export default ConfirmEmailChange;
